import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Stack, InputAdornment, IconButton, useTheme, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router';

import { PATH_AUTH } from '@app/routes/paths';
import { FormProvider, Iconify, RHFTextField } from '@app/components';
import { useRequestState } from '@app/hooks';
import { Strings } from '@app/constants';
import { SvgColor } from '@app/components/svg-color';
import { typography } from '@app/theme/typography';
import type { ISetPasswordSchema } from '../types';
import { setPasswordSchema } from '../validators';
import { setPassword } from '../slice';
import { selectSetPasswordState } from '../selectors';

// --------------------------------------------

const SetPasswordForm = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const onClickEyeIcon = () => {
    setShowPassword(!showPassword);
  };

  const onClickEyeIcon2 = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const methods = useForm<ISetPasswordSchema>({
    resolver: yupResolver(setPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = (form: ISetPasswordSchema) => {
    dispatch(setPassword({ ...form, token: searchParams.get('token') ?? '' }));
  };

  const navigateToLogin = useCallback(() => {
    navigate(PATH_AUTH.login);
  }, [navigate]);

  const { loading } = useRequestState({
    stateSelector: selectSetPasswordState,
    errorShown: true,
    successMessageShown: true,
    onSuccess: navigateToLogin,
  });

  return (
    <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
      <Stack>
        <Typography
          fontFamily={typography.fontFamily}
          fontSize={{ xs: '12px', md: '15px' }}
          fontWeight={typography.fontWeightRegular}
        >
          {Strings.field.newPassword}
        </Typography>
        <RHFTextField
          name="password"
          fullWidth
          type={showPassword ? 'text' : 'password'}
          size="small"
          sx={{ marginBottom: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgColor fontSize={'10px'} src={`/assets/icons/ic_password.svg`} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={onClickEyeIcon}>
                  {showPassword ? <Iconify icon="mdi:eye" /> : <Iconify icon="mdi:eye-off" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Typography
          fontFamily={typography.fontFamily}
          fontSize={{ xs: '12px', md: '15px' }}
          fontWeight={typography.fontWeightRegular}
        >
          {Strings.field.confirmNewPassword}
        </Typography>
        <RHFTextField
          name="confirmPassword"
          fullWidth
          size="small"
          type={showConfirmPassword ? 'text' : 'password'}
          sx={{ marginBottom: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgColor fontSize={'10px'} src={`/assets/icons/ic_password.svg`} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={onClickEyeIcon2}>
                  {showConfirmPassword ? <Iconify icon="mdi:eye" /> : <Iconify icon="mdi:eye-off" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <LoadingButton
          size="large"
          variant="contained"
          fullWidth
          type="submit"
          loading={loading}
          sx={{ marginTop: 10, marginBottom: 10, backgroundColor: theme.palette.common.black }}
        >
          {Strings.button.reset}
        </LoadingButton>
      </Stack>
    </FormProvider>
  );
};

export { SetPasswordForm };
